import { lazy, Suspense } from 'react';
import type { ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAppSelector } from '@store/helpers';
import MainLayout from '@layout/MainLayout/MainLayout';

const LoginPage = lazy(() => import('@pages/LoginPage').then(module => ({ default: module.LoginPage })));
const DashboardPage = lazy(() =>
  import('@pages/DashboardPage').then(module => ({ default: module.DashboardPage }))
);
const OrganizationsPage = lazy(() =>
  import('@pages/OrganizationsPage').then(module => ({ default: module.OrganizationsPage }))
);
const ProjectsPage = lazy(() =>
  import('@pages/ProjectsPage').then(module => ({ default: module.ProjectsPage }))
);
const AccessPage = lazy(() =>
  import('@pages/AccessPage').then(module => ({ default: module.AccessPage }))
);
const SubscriptionsPage = lazy(() =>
  import('@pages/SubscriptionsPage').then(module => ({ default: module.SubscriptionsPage }))
);
const InvoicesPage = lazy(() =>
  import('@pages/InvoicesPage').then(module => ({ default: module.InvoicesPage }))
);
const FeatureFlagsPage = lazy(() =>
  import('@pages/FeatureFlagsPage').then(module => ({ default: module.FeatureFlagsPage }))
);
const InboxPage = lazy(() =>
  import('@pages/InboxPage').then(module => ({ default: module.InboxPage }))
);
const NotificationsPage = lazy(() =>
  import('@pages/NotificationsPage').then(module => ({ default: module.NotificationsPage }))
);
const BundlesPage = lazy(() =>
  import('@pages/BundlesPage').then(module => ({ default: module.BundlesPage }))
);
const InstallsPage = lazy(() =>
  import('@pages/InstallsPage').then(module => ({ default: module.InstallsPage }))
);
const UsersPage = lazy(() =>
  import('@pages/UsersPage').then(module => ({ default: module.UsersPage }))
);
const OperationsPage = lazy(() =>
  import('@pages/OperationsPage').then(module => ({ default: module.OperationsPage }))
);
const DropletOpsPage = lazy(() =>
  import('@pages/DropletOpsPage').then(module => ({ default: module.DropletOpsPage }))
);
const AiPage = lazy(() => import('@pages/AiPage').then(module => ({ default: module.AiPage })));
const AdminsPage = lazy(() =>
  import('@pages/AdminsPage').then(module => ({ default: module.AdminsPage }))
);

const lazyPage = (page: ReactElement) => <Suspense fallback={null}>{page}</Suspense>;

const RequireAuth = ({ children }: { children: ReactElement }) => {
  const token = useAppSelector(s => s.adminAuth.token);
  const role = useAppSelector(s => s.adminAuth.admin?.role);
  if (!token || role !== 'superadmin') {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AppRouter = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/login" element={lazyPage(<LoginPage />)} />
      <Route
        element={
          <RequireAuth>
            <MainLayout />
          </RequireAuth>
        }
      >
        <Route index element={lazyPage(<DashboardPage />)} />
        <Route path="organizations" element={lazyPage(<OrganizationsPage />)} />
        <Route path="projects" element={lazyPage(<ProjectsPage />)} />
        <Route path="access" element={lazyPage(<AccessPage />)} />
        <Route path="subscriptions" element={lazyPage(<SubscriptionsPage />)} />
        <Route path="invoices" element={lazyPage(<InvoicesPage />)} />
        <Route path="feature-flags" element={lazyPage(<FeatureFlagsPage />)} />
        <Route path="inbox" element={lazyPage(<InboxPage />)} />
        <Route path="notifications" element={lazyPage(<NotificationsPage />)} />
        <Route path="bundles" element={lazyPage(<BundlesPage />)} />
        <Route path="installs" element={lazyPage(<InstallsPage />)} />
        <Route path="users" element={lazyPage(<UsersPage />)} />
        <Route path="operations" element={lazyPage(<OperationsPage />)} />
        <Route path="do" element={lazyPage(<DropletOpsPage />)} />
        <Route path="ai" element={lazyPage(<AiPage />)} />
        <Route path="admins" element={lazyPage(<AdminsPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </BrowserRouter>
);

export default AppRouter;
